import { Box, Paper, Typography, useTheme } from "@mui/material";
import React from "react";

type Props = {
    title: string;
    subtitle?: React.ReactNode;
    /** Right-aligned action rendered in the card header (e.g. an Edit/Add button). */
    headerAction?: React.ReactNode;
    /** Removes body padding — useful when the body is a full-bleed table. */
    disableBodyPadding?: boolean;
    children?: React.ReactNode;
};

/**
 * Reusable card container for the Client Brain page sections: a titled Paper panel
 * with an optional subtitle and a right-aligned header action.
 */
const ClientBrainSectionCard: React.FC<Props> = ({
    title,
    subtitle,
    headerAction,
    disableBodyPadding,
    children,
}) => {
    const theme = useTheme();

    return (
        <Paper
            elevation={0}
            sx={{
                width: "100%",
                border: `1px solid ${theme.palette.gray[200]}`,
                borderRadius: "8px",
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 2,
                    padding: theme.spacing(2.5),
                    paddingBottom: subtitle ? theme.spacing(1.5) : undefined,
                }}
            >
                <Box>
                    <Typography
                        sx={{
                            fontSize: "16px",
                            fontWeight: 600,
                            color: theme.palette.gray[700],
                        }}
                    >
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography
                            sx={{
                                fontSize: "13px",
                                color: theme.palette.gray[500],
                                marginTop: theme.spacing(0.5),
                                maxWidth: "720px",
                            }}
                        >
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                {headerAction && (
                    <Box sx={{ flexShrink: 0 }}>{headerAction}</Box>
                )}
            </Box>
            {children && (
                <Box
                    sx={{
                        padding: disableBodyPadding
                            ? 0
                            : `0 ${theme.spacing(2.5)} ${theme.spacing(2.5)}`,
                    }}
                >
                    {children}
                </Box>
            )}
        </Paper>
    );
};

export default ClientBrainSectionCard;
